import {Component, OnInit} from '@angular/core';
import {AlertService} from "../services";
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';

import { Router, ActivatedRoute } from '@angular/router';
import { first } from 'rxjs/operators';
import {MatDialog} from "@angular/material/dialog";
import {ComponentTitleService} from "../services/componentTitle.service";
import {FuseTranslationLoaderService} from "../../../@fuse/services/translation-loader.service";

import { locale as polish } from './i18n/pl';
import {fuseAnimations} from "../../../@fuse/animations";
import {MatSnackBar} from "@angular/material/snack-bar";
import {Service} from "../models/service";
import {MessageTemplateList} from "../models/messageTemplateList";
import {DiscountService} from "./service/discount.service";
import {Discount} from "./model/discount";
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from "@angular/material/core";
import {MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter} from "@angular/material-moment-adapter";
import {VoucherTemplateGroupList} from "../models/voucherTemplateGroupList";

export const MY_FORMATS = {
    parse: {
        dateInput: 'DD.MM.YYYY',
    },
    display: {
        dateInput: 'DD.MM.YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM-YYYY',
    },
};

@Component({
    templateUrl: './discountForm.component.html',
    styleUrls: ['./discountForm.component.scss'],
    animations   : fuseAnimations,
    providers: [
        // `MomentDateAdapter` can be automatically provided by importing `MomentDateModule` in your
        // application's root module. We provide it at the component level here, due to limitations of
        // our example generation script.
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
        },

        {provide: MAT_DATE_FORMATS, useValue: MY_FORMATS},
    ],
})

export class DiscountFormComponent implements OnInit {

    form: FormGroup;
    loading = false;
    submitted = false;
    id: string;
    isAddMode: boolean;
    dialogRef: any;
    availableServices: Service[];
    discount: Discount;
    groupsList: VoucherTemplateGroupList;
    templates: MessageTemplateList;
    hide: boolean = true;
    types: any[] = [
        {
            "id": "percent",
            "name": "Percent"
        },
        {
            "id": "value",
            "name": "Value"
        },
    ];

    constructor(
        public _matDialog: MatDialog,
        private formBuilder: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private alertService: AlertService,
        private componentTitleService: ComponentTitleService,
        private translationLoader: FuseTranslationLoaderService,
        private _matSnackBar: MatSnackBar,
        private discountService: DiscountService,
    ) {
        this.translationLoader.loadTranslations(polish);

        componentTitleService.clearTitle();
        componentTitleService.addPart('Discounts');
    }

    ngOnInit() {
        this.discount = new Discount();
        this.id = this.route.snapshot.params['id'];
        this.isAddMode = !this.id;
        this.groupsList = this.route.snapshot.data.voucherGroups;

        if (this.isAddMode) {
            this.componentTitleService.addPart('New');
        } else {
            this.componentTitleService.addPart('Edit');
        }

        this.form = this.formBuilder.group({
            name: ['', Validators.required],
            value: ['', Validators.required],
            minimalQuantityOfProducts: [''],
            minimalQuantityOfVouchers: [''],
            minimalQuantityOfLines: [''],
            availableOneTimeFirstDay: [false],
            canConnect: [false],
            type: ['', Validators.required],
            netPriceDiscount: [false],
            grossPriceDiscount: [false],
            fromDate: [''],
            toDate: [''],
            voucherGroups: [''],
        });

        if (!this.isAddMode) {
            this.discount = this.route.snapshot.data.discount;
            this.f.name.setValue(this.discount.name);
            this.f.value.setValue(this.discount.value);
            this.f.minimalQuantityOfProducts.setValue(this.discount.minimalQuantityOfProducts);
            this.f.minimalQuantityOfVouchers.setValue(this.discount.minimalQuantityOfVouchers);
            this.f.minimalQuantityOfLines.setValue(this.discount.minimalQuantityOfLines);
            this.f.availableOneTimeFirstDay.setValue(this.discount.availableOneTimeFirstDay);
            this.f.canConnect.setValue(this.discount.canConnect);
            this.f.type.setValue(this.discount.type);
            this.f.netPriceDiscount.setValue(this.discount.netPriceDiscount);
            this.f.grossPriceDiscount.setValue(this.discount.grossPriceDiscount);
            this.f.fromDate.setValue(this.discount.fromDate);
            this.f.toDate.setValue(this.discount.toDate);
            this.f.voucherGroups.setValue(this.discount.voucherGroups);
        }
    }

    // convenience getter for easy access to form fields
    get f() { return this.form.controls; }

    onSubmit() {
        this.submitted = true;

        // reset alerts on submit
        // this.alertService.clear();

        // stop here if form is invalid
        if (this.form.invalid) {
            return;
        }

        this.loading = true;

        if (this.isAddMode) {
            this.createDiscountAccount();
        } else {
            this.updateDiscountAccount();
        }
    }

    private createDiscountAccount() {
        this.discountService.create(this.form.value)
            .pipe(first())
            .subscribe(
                data => {
                    this._matSnackBar.open('Discount added successfully', 'OK', {
                        verticalPosition: 'top',
                        duration        : 5000,
                        panelClass: ['snackbar-success']
                    });
                    this.router.navigate(['.', { relativeTo: this.route }]);
                },
                error => {
                    this.loading = false;
                    this._matSnackBar.open('Discount creation error - ' + error, 'OK', {
                        verticalPosition: 'top',
                        duration        : 5000,
                        panelClass: ['snackbar-error']
                    });
                });
    }

    private updateDiscountAccount() {
        this.discountService.update(this.id, this.form.value)
            .pipe(first())
            .subscribe(
                data => {
                    this._matSnackBar.open('Update successful', 'OK', {
                        verticalPosition: 'top',
                        duration        : 5000,
                        panelClass: ['snackbar-success']
                    });
                    this.router.navigate(['..', { relativeTo: this.route }]);
                },
                error => {
                    this._matSnackBar.open('Discount update error - ' + error, 'OK', {
                        verticalPosition: 'top',
                        duration        : 5000,
                        panelClass: ['snackbar-error']
                    });
                    this.loading = false;
                });
    }

    compareObjects(o1: any, o2: any): boolean {
        return o1.name === o2.name && o1.id === o2.id;
    }
}
